import React, { useRef, useState } from 'react';
import {
  Button,
  Paper,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Collapse,
  TextField,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import axios from 'axios';
import FileDropzone from '../common/FileDropzone';
import DisabledReason from '../common/DisabledReason';
import useFokusNachLaden from '../common/hooks/useFokusNachLaden';
import UploadSummaryModal from './UploadSummaryModal';
import { formatBytes } from '../../utils/format';

const API = process.env.REACT_APP_API_BASE_URL;

export default function FulfillmentUpload({ onUploaded, onRebuildAllocation }) {
  const [files, setFiles] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [serverResp, setServerResp] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  // 🔴 K-F1 (2026-10-10): der Knopf sperrt sich beim Hochladen selbst und
  // verliert dabei den Fokus — der Haken holt ihn danach zurueck.
  const uploadKnopf = useRef(null);
  useFokusNachLaden(uploadKnopf, uploading);

  const handleRemoveFile = (file) => {
    setFiles(files.filter(f => f !== file));
  };

  const handleUpload = async () => {
    if (!files.length) {
      setError('Please select at least one CSV file.');
      return;
    }

    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (invoiceNumber) formData.append('invoice_number', invoiceNumber);

    setUploading(true);
    setError('');
    setServerResp(null);

    try {
      const response = await axios.post(`${API}/api/fulfillment/upload`, formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      setServerResp(response.data);
      if (response.data.summary) {
        setShowSummary(true);
      }
      onUploaded(); // Callback to refresh data on parent
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setFiles([]);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Upload CSV
      </Typography>
      <FileDropzone
        multiple
        onFiles={(files) => setFiles((prev) => [...prev, ...files])}
        label="Drag & drop files here, or click to select"
        sx={{ mb: 2 }}
      />

      <Collapse in={files.length > 0}>
        <List dense>
          {files.map((file, index) => (
            <ListItem
              key={index}
              secondaryAction={
                <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveFile(file)}>
                  <CloseIcon />
                </IconButton>
              }
            >
              <ListItemIcon>
                <DescriptionIcon />
              </ListItemIcon>
              <ListItemText primary={file.name} secondary={formatBytes(file.size)} />
            </ListItem>
          ))}
        </List>
      </Collapse>

      <TextField
        fullWidth
        label="Invoice Number (optional)"
        value={invoiceNumber}
        onChange={(e) => setInvoiceNumber(e.target.value)}
        sx={{ my: 1 }}
      />
      {uploading && <LinearProgress variant="determinate" value={uploadProgress} sx={{ my: 1 }} />}
      {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}
      {serverResp && !error && (
        <Alert severity="success" sx={{ my: 1 }}>
          Upload successful!
          <Button size="small" onClick={() => setShowSummary(true)} sx={{ ml: 2 }}>
            View summary
          </Button>
        </Alert>
      )}

      <DisabledReason
        reason={uploading
          ? null
          : files.length === 0 ? 'Choose at least one file first.' : null}
      >
        {/* 🔴 K-F1 (2026-10-10): der Lauf zeigt sich am Knopf selbst — Ring
            statt Wortwechsel. `loadingPosition="start"` haelt die Beschriftung
            lesbar (die Vorgabe `center` setzt `color: transparent`). Die
            RUHE-Beschriftung bleibt stehen; der Wortwechsel ist weg. */}
        <Button
          ref={uploadKnopf}
          fullWidth
          variant="contained"
          onClick={handleUpload}
          disabled={uploading || files.length === 0}
          loading={uploading}
          loadingPosition="start"
          aria-busy={uploading || undefined}
          sx={{ mt: 2 }}
        >
          Upload & analyze
        </Button>
      </DisabledReason>

      <UploadSummaryModal
        open={showSummary}
        onClose={() => setShowSummary(false)}
        data={serverResp?.summary}
        onRebuildAllocation={onRebuildAllocation}
      />
    </Paper>
  );
}
